import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import AdminDashboard from './components/AdminDashboard';
import AdminPasswordModal from './components/AdminPasswordModal';
import AdminAuthGuard from './components/AdminAuthGuard';
import UserLoginPage from './pages/UserLoginPage';
import UserRegistrationPage from './pages/UserRegistrationPage';
import UserDashboard from './components/UserDashboard';
import QRLoginPage from './pages/QRLoginPage';
import SSOLoginPage from './pages/SSOLoginPage';
import './i18n';
import DonationPage from './pages/DonationPage';
import CustomerVerificationPage from './pages/CustomerVerificationPage';
import OTPVerificationPage from './pages/OTPVerificationPage';
import EnterAmountPage from './pages/EnterAmountPage';
import PaymentGatewayPage from './pages/PaymentGatewayPage';
import PaymentProcessingPage from './pages/PaymentProcessingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import OrderStatusPage from './pages/OrderStatusPage';
import OrdersHistoryPage from './pages/OrdersHistoryPage';
import ApiLogPage from './pages/ApiLogPage';
import APIHealthPage from './pages/APIHealthPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import CronManagement from './components/CronManagement';
import LanguageSwitcher from './components/LanguageSwitcher';
import { setCurrentUser, clearCurrentUser, getCurrentUser } from './utils/auth';
import jwtService from './services/jwtService';
import './i18n';

interface SystemData {
  backendStatus: string;
  databaseStatus: string;
  timestamp: string;
  config?: {
    ssoLoginEnabled?: boolean;
  };
}

function App() {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [data, setData] = useState<SystemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ssoLoginEnabled, setSsoLoginEnabled] = useState<boolean>(false);
  
  // Admin state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [adminData, setAdminData] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
  
  // User state
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/data`);
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const result = await response.json();
        setData(result);
        // Extract SSO login enabled status from config (default: false)
        // Handle both boolean and string values
        if (result.config && result.config.ssoLoginEnabled !== undefined) {
          const ssoEnabled = result.config.ssoLoginEnabled;
          // Convert to boolean: handle both string "true"/"false" and boolean true/false
          const isEnabled = ssoEnabled === true || ssoEnabled === 'true' || ssoEnabled === '1';
          setSsoLoginEnabled(isEnabled);
        } else {
          // Default to false if not provided
          setSsoLoginEnabled(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load user from localStorage on app startup
  useEffect(() => {
    const savedUser = getCurrentUser();
    const savedToken = jwtService.getUserToken();
    console.log('🔍 Loading saved user from localStorage:', savedUser);
    console.log('🔍 Loading saved JWT token:', savedToken ? 'Token exists' : 'No token');
    
    if (savedUser) {
      setUserData(savedUser);
      console.log('✅ User loaded from localStorage');
      
      // If we have a user but no JWT token, clear the user (token expired)
      if (!savedToken) {
        console.log('⚠️ User exists but no valid JWT token, clearing user data');
        setUserData(null);
        clearCurrentUser();
      }
    }
  }, []);

  // Admin handlers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAdminLoginSuccess = (admin: any) => {
    setAdminData(admin);
    setShowAdminPanel(true);
  };

  const handleAdminLogout = () => {
    // Clear admin data from localStorage
    localStorage.removeItem('adminData');
    setAdminData(null);
    setShowAdminPanel(false);
    // Redirect to home page
    window.location.href = '/';
  };

  const handleAdminPasswordSuccess = (admin: any) => {
    setAdminData(admin);
    setShowAdminPanel(true);
    setShowAdminPasswordModal(false);
    // Navigate to admin route
    window.location.href = '/admin';
  };

  const handleAdminPasswordClose = () => {
    setShowAdminPasswordModal(false);
  };

  // User handlers
  const handleUserLoginSuccess = (user: any, tokenData?: any) => {
    console.log('🔍 User login success, setting user data:', user);
    console.log('🔍 Token data:', tokenData);
    
    // Store user data in state
    setUserData(user);
    setCurrentUser(user);
    
    // Store JWT token if provided
    if (tokenData) {
      jwtService.storeUserToken({
        id: user.id || user.unique_id,
        unique_id: user.unique_id,
        mobile_number: user.mobile_number,
        email_id: user.email_id,
        user_name: user.user_name,
        token: tokenData.token,
        expiresAt: tokenData.expiresAt,
        expiresIn: tokenData.expiresIn
      });
      console.log('✅ JWT token stored for user');
    }
    
    console.log('✅ User data set in state and localStorage');
  };

  const handleUserLogout = () => {
    setUserData(null);
    clearCurrentUser();
    jwtService.clearUserToken();
    console.log('✅ User logged out and JWT token cleared');
  };

  const handleRegistrationSuccess = () => {
    // Registration successful, user will be redirected to login
  };

  // Main page component
  const MainPage = () => (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-4xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        
        {/* Language Switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12 w-full">
          <div className="flex items-center justify-center mb-6 sm:mb-8">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-full border-2 border-white flex items-center justify-center">
              <img 
                src="/images/Asset2.png" 
                alt="Temple Logo" 
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
              />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-4">{t('pages.mainPage.title')}</h1>
        </div>

        {/* Status Cards */}
        {loading && (
          <div className="max-w-md mx-auto mb-8 bg-white/10 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-3"></div>
            <p className="text-white text-center">{t('common.loading')}</p>
          </div>
        )}

        {error && (
          <div className="max-w-md mx-auto mb-8 bg-red-500/20 rounded-lg p-4 text-center">
            <p className="text-red-200 text-center">{t('common.error')}: {error}</p>
          </div>
        )}


        {/* Action Buttons */}
        <div className="max-w-4xl mx-auto w-full">
          <div className={`grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 w-full justify-items-center ${
            ssoLoginEnabled ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
          }`}>
            {/* Admin Panel Button */}
            <button
              onClick={() => window.location.href = '/admin'}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 sm:py-6 px-6 sm:px-8 rounded-lg transition duration-200 shadow-lg hover:shadow-xl w-full max-w-xs sm:max-w-sm"
            >
              <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3">
                <span className="text-2xl sm:text-3xl">👨‍💼</span>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-semibold">{t('pages.mainPage.adminButton')}</div>
                  <div className="text-xs sm:text-sm opacity-90">{t('pages.mainPage.adminDescription')}</div>
                </div>
              </div>
            </button>

            {/* User Portal Button */}
            <a
              href="/users"
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 sm:py-6 px-6 sm:px-8 rounded-lg transition duration-200 shadow-lg hover:shadow-xl block w-full max-w-xs sm:max-w-sm text-center"
            >
              <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3">
                <span className="text-2xl sm:text-3xl">🙏</span>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-semibold">{t('pages.mainPage.homeButton')}</div>
                  <div className="text-xs sm:text-sm opacity-90">{t('pages.mainPage.homeDescription')}</div>
                </div>
              </div>
            </a>

            {/* SSO Login Button - Only show if enabled in backend config */}
            {ssoLoginEnabled && (
              <button
                onClick={() => window.location.href = '/sso'}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 sm:py-6 px-6 sm:px-8 rounded-lg transition duration-200 shadow-lg hover:shadow-xl w-full max-w-xs sm:max-w-sm"
              >
                <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3">
                  <span className="text-2xl sm:text-3xl">🔐</span>
                  <div className="text-center">
                    <div className="text-lg sm:text-xl font-semibold">{t('pages.mainPage.ssoButton')}</div>
                    <div className="text-xs sm:text-sm opacity-90">{t('pages.mainPage.ssoDescription')}</div>
                  </div>
                </div>
              </button>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // User Login Page Component
  const UserLoginRoute = () => (
    <UserLoginPage
      onLoginSuccess={handleUserLoginSuccess}
    />
  );

  // User Registration Page Component
  const UserRegistrationRoute = () => (
    <UserRegistrationPage
      onRegistrationSuccess={handleRegistrationSuccess}
    />
  );

  // User Dashboard Component
  const UserDashboardRoute = () => (
    <UserDashboard
      userData={userData}
      onLogout={handleUserLogout}
    />
  );

  return (
    <Router>
      <div 
        className="App app-background" 
        style={{
          backgroundColor: 'white',
          backgroundImage: 'url(/images/Asset5.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          minHeight: '100vh'
        }}
      >
        <Routes>
          {/* Main route */}
          <Route path="/" element={<MainPage />} />
          
          {/* User routes */}
          <Route path="/users" element={<UserLoginRoute />} />
          <Route path="/users/register" element={<UserRegistrationRoute />} />
          <Route path="/sso" element={<SSOLoginPage />} />
          <Route path="/users/dashboard" element={<UserDashboardRoute />} />
          <Route path="/users/donation" element={<DonationPage />} />
          <Route path="/qr-login" element={<QRLoginPage />} />
          <Route path="/verification" element={<CustomerVerificationPage />} />
          <Route path="/otp-verification" element={<OTPVerificationPage />} />
          <Route path="/enter-amount" element={<EnterAmountPage />} />
          <Route path="/payment-gateway" element={<PaymentGatewayPage />} />
          <Route path="/payment-processing" element={<PaymentProcessingPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/users/orders" element={<OrdersHistoryPage />} />
          <Route path="/users/orders/:orderId" element={<OrderStatusPage />} />
          
          {/* Admin routes - Protected with AuthGuard */}
          <Route path="/admin" element={
            <AdminAuthGuard onLogout={handleAdminLogout}>
              <AdminDashboard />
            </AdminAuthGuard>
          } />
          
          {/* Cron Management route - Protected */}
          <Route path="/cron" element={
            <AdminAuthGuard onLogout={handleAdminLogout}>
              <CronManagement />
            </AdminAuthGuard>
          } />
          
          {/* API/Webhook Logs route - Protected */}
          <Route path="/apilog" element={
            <AdminAuthGuard onLogout={handleAdminLogout}>
              <ApiLogPage />
            </AdminAuthGuard>
          } />
          
          {/* API Health Monitor route - Protected */}
          <Route path="/api-health" element={
            <AdminAuthGuard onLogout={handleAdminLogout}>
              <APIHealthPage />
            </AdminAuthGuard>
          } />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {/* Admin Password Modal */}
      <AdminPasswordModal
        isOpen={showAdminPasswordModal}
        onClose={handleAdminPasswordClose}
        onSuccess={handleAdminPasswordSuccess}
      />
    </Router>
  );
}

export default App; 